import React,{createContext,useContext,useState,useEffect} from 'react';
import {supabase} from '../lib/supabase';
import {sendConfirmationEmails} from '../utils/emailService';
import {isToday,isPast,parseISO,format,addDays,startOfWeek,getDay,isAfter,set,addWeeks,isBefore,isWeekend,isSameDay,endOfDay,startOfDay,isWithinInterval} from 'date-fns';
import {de} from 'date-fns/locale';

const MenuContext=createContext();

export const useMenu=()=> {
  const context=useContext(MenuContext);
  if (!context) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
};

export const MenuProvider=({children})=> {
  const [weeklyMenus,setWeeklyMenus]=useState({});
  const [nextWeeklyMenus,setNextWeeklyMenus]=useState({});
  const [registrations,setRegistrations]=useState([]);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState(null);
  const [emailServiceReady,setEmailServiceReady]=useState(true);
  const [menuDates,setMenuDates]=useState({});
  const [selectedMenu,setSelectedMenu]=useState(null);
  const [availableDays,setAvailableDays]=useState(['dienstag','mittwoch','donnerstag','freitag']);
  const [holidays,setHolidays]=useState([]);
  const [serviceTime,setServiceTime]=useState('12:00');
  const [defaultServiceTime,setDefaultServiceTime]=useState(null);

  // ✅ VERBESSERTE FUNKTION: Lade Service-Zeit aus den Servicezeiten mit Aktualisierung
  const loadServiceTime=async ()=> {
    try {
      console.log('🕐 Loading service times configuration...');
      
      // ✅ Lade aus der service_times Tabelle statt service_config
      const {data,error}=await supabase
        .from('service_times_a7b9c8d6e5')
        .select('*')
        .eq('is_active',true)
        .eq('is_default',true)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading service time:',error);
        // Fallback: Lade die erste aktive Servicezeit
        const {data: fallbackData,error: fallbackError}=await supabase
          .from('service_times_a7b9c8d6e5')
          .select('*')
          .eq('is_active',true)
          .order('time',{ascending: true})
          .limit(1)
          .single();
        
        if (fallbackData) {
          console.log('✅ Using fallback service time:',fallbackData.time);
          setServiceTime(fallbackData.time);
          setDefaultServiceTime(fallbackData);
          return;
        }
        
        // Ultimativer Fallback
        setServiceTime('12:00');
        setDefaultServiceTime({time: '12:00',label: 'Standard'});
        return;
      }

      if (data) {
        console.log('✅ Loaded default service time:',data);
        setServiceTime(data.time);
        setDefaultServiceTime(data);
      } else {
        console.log('📝 No default service time found,loading first available...');
        
        // Lade die erste verfügbare aktive Servicezeit
        const {data: firstData,error: firstError}=await supabase
          .from('service_times_a7b9c8d6e5')
          .select('*')
          .eq('is_active',true)
          .order('time',{ascending: true})
          .limit(1)
          .single();
        
        if (firstData) {
          console.log('✅ Using first available service time:',firstData.time);
          setServiceTime(firstData.time);
          setDefaultServiceTime(firstData);
        } else {
          // Ultimativer Fallback
          console.log('📝 No service times found,using default 12:00');
          setServiceTime('12:00');
          setDefaultServiceTime({time: '12:00',label: 'Standard'});
        }
      }
    } catch (err) {
      console.error('Error in loadServiceTime:',err);
      setServiceTime('12:00');
      setDefaultServiceTime({time: '12:00',label: 'Standard'});
    }
  };

  // ✅ VERBESSERTE FUNKTION: Formatiere Service-Zeit für Anzeige (ohne Sekunden)
  const getFormattedServiceTime=()=> {
    if (!defaultServiceTime) {
      return '12:00 Uhr';
    }
    
    // Entferne Sekunden falls vorhanden (z.B. "11:30:00" -> "11:30")
    const timeWithoutSeconds = defaultServiceTime.time && defaultServiceTime.time.includes(':') 
      ? defaultServiceTime.time.split(':').slice(0, 2).join(':')
      : (defaultServiceTime.time || '12:00');
    
    // ✅ NUR die Zeit ohne Label zurückgeben für die Homepage
    return `${timeWithoutSeconds} Uhr`;
  };

  // ✅ NEUE FUNKTION: Formatiere Service-Zeit MIT Label (für Admin-Bereiche)
  const getFormattedServiceTimeWithLabel=()=> {
    if (!defaultServiceTime) {
      return '12:00 Uhr';
    }
    
    // Entferne Sekunden falls vorhanden
    const timeWithoutSeconds = defaultServiceTime.time && defaultServiceTime.time.includes(':') 
      ? defaultServiceTime.time.split(':').slice(0, 2).join(':')
      : (defaultServiceTime.time || '12:00');
    
    // Mit Label nur wenn es nicht "Standard" ist
    if (defaultServiceTime.label && defaultServiceTime.label !== 'Standard') {
      return `${timeWithoutSeconds} Uhr (${defaultServiceTime.label})`;
    }
    
    return `${timeWithoutSeconds} Uhr`;
  };

  // ✅ NEUE FUNKTION: Hole die aktuelle Standard-Servicezeit (mit aktuellen Daten)
  const getCurrentServiceTime=()=> {
    return defaultServiceTime || {time: serviceTime,label: 'Standard'};
  };

  // ✅ NEUE FUNKTION: Aktualisiere Service-Zeit (für Admin-Änderungen)
  const refreshServiceTime=async ()=> {
    await loadServiceTime();
  };

  // ✅ ERWEITERTE FUNKTION: Lade Feiertage und Ferien aus der Datenbank
  const loadHolidays=async ()=> {
    try {
      console.log('🎄 Loading holidays and vacations...');
      const {data,error}=await supabase
        .from('holidays_a7b9c8d6e5')
        .select('*')
        .eq('is_active',true);

      if (error) {
        console.error('Error loading holidays:',error);
        return;
      }

      console.log('✅ Loaded holidays and vacations:',data);
      setHolidays(data || []);
    } catch (err) {
      console.error('Error in loadHolidays:',err);
    }
  };

  // ✅ ERWEITERTE FUNKTION: Prüfe ob ein Datum ein Feiertag ist oder in Ferien liegt
  const isDateHoliday=(dateString)=> {
    if (!dateString || !holidays.length) return false;

    const checkDate=parseISO(dateString);
    
    return holidays.some(holiday=> {
      if (!holiday.is_active) return false;

      const startDate=parseISO(holiday.start_date);

      // Für Einzeltag-Feiertage (end_date ist null)
      if (!holiday.end_date) {
        // Direkter Vergleich des Datums
        if (holiday.start_date===dateString) {
          return true;
        }
        
        // Für wiederkehrende Feiertage: Vergleiche Monat und Tag
        if (holiday.is_recurring) {
          return (
            startDate.getMonth()===checkDate.getMonth() &&
            startDate.getDate()===checkDate.getDate()
          );
        }
      } else {
        // Für Ferienperioden (start_date bis end_date)
        const endDate=parseISO(holiday.end_date);
        
        if (holiday.is_recurring) {
          // Für wiederkehrende Ferienperioden: Prüfe ob das Datum im entsprechenden Zeitraum des aktuellen Jahres liegt
          const currentYear=checkDate.getFullYear();
          const yearlyStartDate=new Date(currentYear,startDate.getMonth(),startDate.getDate());
          const yearlyEndDate=new Date(currentYear,endDate.getMonth(),endDate.getDate());
          
          // Handle year-crossing vacations (e.g.,Christmas holidays)
          if (yearlyEndDate < yearlyStartDate) {
            return checkDate >=yearlyStartDate || checkDate <=yearlyEndDate;
          } else {
            return isWithinInterval(checkDate,{start: yearlyStartDate,end: yearlyEndDate});
          }
        } else {
          // Für einmalige Ferienperioden: Direkter Intervall-Check
          return isWithinInterval(checkDate,{start: startDate,end: endDate});
        }
      }

      return false;
    });
  };

  // ✅ NEUE FUNKTION: Lade verfügbare Tage aus der Datenbank
  const loadAvailableDays=async ()=> {
    try {
      console.log('🗓️ Loading available days configuration...');
      const {data,error}=await supabase
        .from('menu_day_config_a7b9c8d6e5')
        .select('*')
        .eq('is_active',true)
        .order('day_order');

      if (error) {
        console.error('Error loading day config:',error);
        // Fallback zu Standardwerten wenn Tabelle nicht existiert
        return;
      }

      if (data && data.length > 0) {
        const activeDays=data.map(config=> config.day_name);
        console.log('✅ Loaded active days:',activeDays);
        setAvailableDays(activeDays);
      }
    } catch (err) {
      console.error('Error in loadAvailableDays:',err);
      // Fallback zu Standardwerten
    }
  };

  // ✅ NEUE FUNKTION: Aktualisiere verfügbare Tage
  const updateAvailableDays=async (dayConfigs)=> {
    try {
      console.log('🔄 Updating available days:',dayConfigs);

      // Lösche alle bestehenden Konfigurationen
      await supabase
        .from('menu_day_config_a7b9c8d6e5')
        .delete()
        .neq('id','00000000-0000-0000-0000-000000000000');

      // Füge neue Konfigurationen hinzu
      const configsToInsert=dayConfigs.map((config,index)=> ({
        day_name: config.day,
        is_active: config.active,
        day_order: config.order || index + 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));

      const {error}=await supabase
        .from('menu_day_config_a7b9c8d6e5')
        .insert(configsToInsert);

      if (error) {
        throw error;
      }

      // Aktualisiere lokalen State
      const activeDays=dayConfigs
        .filter(config=> config.active)
        .sort((a,b)=> (a.order || 0) - (b.order || 0))
        .map(config=> config.day);

      setAvailableDays(activeDays);
      console.log('✅ Available days updated:',activeDays);

      return {success: true};
    } catch (err) {
      console.error('Error updating available days:',err);
      throw err;
    }
  };

  // Funktion zum Berechnen der Daten für die Wochentage
  const calculateMenuDates=(isNextWeek=false)=> {
    const today=new Date();

    // ✅ DYNAMISCHES MAPPING basierend auf verfügbaren Tagen
    const daysMap={
      'montag': 1,
      'dienstag': 2,
      'mittwoch': 3,
      'donnerstag': 4,
      'freitag': 5,
      'samstag': 6,
      'sonntag': 0
    };

    const dates={};

    // Bestimme den Starttag der aktuellen Woche (Montag)
    let weekStart=startOfWeek(today,{weekStartsOn: 1}); // 1 für Montag

    // Wenn es für nächste Woche ist,füge 7 Tage hinzu
    if (isNextWeek) {
      weekStart=addDays(weekStart,7);
    }

    // ✅ NUR für verfügbare Tage das Datum berechnen
    availableDays.forEach(day=> {
      const dayNumber=daysMap[day];
      if (dayNumber !== undefined) {
        let date;
        if (dayNumber===0) { // Sonntag
          date=addDays(weekStart,6);
        } else {
          date=addDays(weekStart,dayNumber - 1);
        }
        dates[day]=format(date,'yyyy-MM-dd');
      }
    });

    return dates;
  };

  // ✅ KORRIGIERTE Funktion zum Berechnen der Daten für die nächsten 5 verfügbare Tage ab einem bestimmten Tag
  const calculateFiveDayMenuDates=(startDay,isNextWeek=false)=> {
    console.log('🔍 calculateFiveDayMenuDates called with:',{startDay,isNextWeek,availableDays});

    // ✅ DYNAMISCHES MAPPING basierend auf verfügbaren Tagen
    const daysMap={
      'montag': 1,
      'dienstag': 2,
      'mittwoch': 3,
      'donnerstag': 4,
      'freitag': 5,
      'samstag': 6,
      'sonntag': 0
    };

    const today=new Date();
    let weekStart=startOfWeek(today,{weekStartsOn: 1}); // Montag als Wochenstart

    if (isNextWeek) {
      weekStart=addDays(weekStart,7);
    }

    // Finde das Datum des angegebenen Starttags
    const startDayNumber=daysMap[startDay];
    if (startDayNumber===undefined) {
      console.error('❌ Invalid start day:',startDay);
      return {};
    }

    let startDate;
    if (startDayNumber===0) { // Sonntag
      startDate=addDays(weekStart,6);
    } else {
      startDate=addDays(weekStart,startDayNumber - 1);
    }

    console.log('📅 Calculated start date:',{
      startDay,
      startDayNumber,
      weekStart: format(weekStart,'yyyy-MM-dd'),
      startDate: format(startDate,'yyyy-MM-dd')
    });

    // ✅ Berechne die nächsten 5 VERFÜGBAREN Tage ab dem Startdatum (exkl. Feiertage/Ferien)
    const nextFiveAvailableDays=[];
    let currentDate=new Date(startDate);
    let attempts=0;
    const maxAttempts=14; // Maximal 2 Wochen vorausschauen

    while (nextFiveAvailableDays.length < 5 && attempts < maxAttempts) {
      const dayOfWeek=currentDate.getDay();
      const dayNames=['sonntag','montag','dienstag','mittwoch','donnerstag','freitag','samstag'];
      const currentDayName=dayNames[dayOfWeek];
      const currentDateString=format(currentDate,'yyyy-MM-dd');

      // ✅ Nur verfügbare Tage hinzufügen UND prüfen ob es kein Feiertag/Ferien ist
      if (availableDays.includes(currentDayName) && !isDateHoliday(currentDateString)) {
        nextFiveAvailableDays.push({
          date: new Date(currentDate),
          dayName: currentDayName
        });
      }

      currentDate=addDays(currentDate,1);
      attempts++;
    }

    console.log('📋 Next five available days (excluding holidays/vacations):',
      nextFiveAvailableDays.map(d=> ({
        day: d.dayName,
        date: format(d.date,'yyyy-MM-dd')
      }))
    );

    // Erzeuge ein Objekt mit den Tagen und ihren Daten
    const dates={};
    nextFiveAvailableDays.forEach(({date,dayName})=> {
      dates[dayName]=format(date,'yyyy-MM-dd');
    });

    console.log('✅ Final 5-day dates (excluding holidays/vacations):',dates);
    return dates;
  };

  // ✅ ERWEITERTE Funktion zur Überprüfung,ob ein Menütag in der Vergangenheit liegt ODER ein Feiertag/Ferien ist
  const isMenuDayPast=(day)=> {
    if (!menuDates[day]) return false;

    const menuDate=parseISO(menuDates[day]);
    const menuDateString=menuDates[day];
    const now=new Date();

    // ✅ FEIERTAGS/FERIEN-PRÜFUNG: Wenn es ein Feiertag/Ferien ist,ist es nicht bestellbar
    if (isDateHoliday(menuDateString)) {
      console.log(`🎄 ${day} (${menuDateString}) ist ein Feiertag/Ferien - nicht bestellbar`);
      return true;
    }

    if (isBefore(endOfDay(menuDate),startOfDay(now))) {
      return true;
    }

    if (isToday(menuDate)) {
      const lunchCutoffTime=set(now,{hours: 13,minutes: 0,seconds: 0});
      return isAfter(now,lunchCutoffTime);
    }

    return false;
  };

  const isMenuDayToday=(day)=> {
    if (!menuDates[day]) return false;
    const menuDate=parseISO(menuDates[day]);
    return isToday(menuDate);
  };

  const formatMenuDate=(day)=> {
    if (!menuDates[day]) return '';
    const menuDate=parseISO(menuDates[day]);
    return format(menuDate,'d. MMMM yyyy',{locale: de});
  };

  // ✅ ERWEITERTE Funktion zur Überprüfung,ob ein Menü bestellbar ist (inkl. Feiertags/Ferien-Prüfung)
  const isMenuOrderable=(menu)=> {
    if (!menu || !menu.date) return false;

    const menuDate=parseISO(menu.date);
    const menuDateString=menu.date;
    const now=new Date();

    // ✅ FEIERTAGS/FERIEN-PRÜFUNG: Wenn es ein Feiertag/Ferien ist,ist es nicht bestellbar
    if (isDateHoliday(menuDateString)) {
      console.log(`🎄 Menu for ${menuDateString} is on a holiday/vacation - not orderable`);
      return false;
    }

    if (isBefore(endOfDay(menuDate),startOfDay(now))) {
      return false;
    }

    if (isToday(menuDate)) {
      const lunchCutoffTime=set(now,{hours: 13,minutes: 0,seconds: 0});
      return !isAfter(now,lunchCutoffTime);
    }

    return true;
  };

  // ✅ ERWEITERTE Funktion zur Überprüfung,ob ein Wochenmenü bestellbar ist (inkl. Feiertags/Ferien-Prüfung)
  const isWeeklyMenuOrderable=(isNextWeek=false,startDay=null)=> {
    const menus=isNextWeek ? nextWeeklyMenus : weeklyMenus;

    // ✅ Nur verfügbare Tage prüfen und Feiertage/Ferien ausschließen
    const orderableDays=availableDays.filter(day=> {
      const menu=menus[day];
      if (!menu) return false;

      // Für nächste Woche: Immer bestellbar (außer Feiertage/Ferien)
      if (isNextWeek) {
        const menuDateString=menu.date;
        return !isDateHoliday(menuDateString);
      }

      // Für aktuelle Woche: Normale Überprüfung + Feiertags/Ferien-Prüfung
      return isMenuOrderable(menu);
    });

    return orderableDays.length >=2;
  };

  // ✅ KORRIGIERTE Funktion zum Ermitteln der bestellbaren Tage für ein Wochenmenü oder 5-Tage-Menü (inkl. Feiertags/Ferien-Prüfung)
  const getOrderableDaysForWeeklyMenu=(isNextWeek=false,startDay=null)=> {
    console.log('🎯 getOrderableDaysForWeeklyMenu called:',{isNextWeek,startDay,availableDays});

    const menus=isNextWeek ? nextWeeklyMenus : weeklyMenus;

    if (startDay) {
      // FLEXIBLE 5-TAGE BUCHUNG: Berechne die nächsten 5 verfügbaren Tage ab dem Starttag (exkl. Feiertage/Ferien)
      const fiveDayDates=calculateFiveDayMenuDates(startDay,isNextWeek);

      // Gebe alle 5 verfügbaren Tage zurück (bereits ohne Feiertage/Ferien)
      const orderableDays=Object.keys(fiveDayDates).sort((a,b)=> {
        // ✅ Dynamische Sortierung basierend auf verfügbaren Tagen
        const dayOrder={};
        availableDays.forEach((day,index)=> {
          dayOrder[day]=index + 1;
        });
        return (dayOrder[a] || 999) - (dayOrder[b] || 999);
      });

      console.log('📊 Orderable days for flexible booking (excluding holidays/vacations):',orderableDays);
      return orderableDays;
    } else {
      // NORMALE WOCHENBUCHUNG: Filtere bestellbare Tage der aktuellen/nächsten Woche (exkl. Feiertage/Ferien)
      const orderableDays=availableDays.filter(day=> {
        const menu=menus[day];
        if (!menu) return false;

        // Für nächste Woche: Feiertags/Ferien-Prüfung
        if (isNextWeek) {
          const menuDateString=menu.date;
          return !isDateHoliday(menuDateString);
        }

        // Für aktuelle Woche: Normale Überprüfung + Feiertags/Ferien-Prüfung
        return isMenuOrderable(menu);
      });

      console.log('📊 Orderable days for weekly booking (excluding holidays/vacations):',orderableDays);
      return orderableDays;
    }
  };

  // Funktion zum Berechnen der Daten für die nächsten 5 verfügbare Tage ab einem bestimmten Tag
  const getCustomWeekDates=(startDay,isNextWeek=false)=> {
    console.log('🗓️ getCustomWeekDates called:',{startDay,isNextWeek});
    const result=calculateFiveDayMenuDates(startDay,isNextWeek);
    console.log('🗓️ getCustomWeekDates result (excluding holidays/vacations):',result);
    return result;
  };

  // Fetch current week menus with improved date logic
  const fetchWeeklyMenus=async ()=> {
    try {
      setLoading(true);
      const {data,error}=await supabase
        .from('weekly_menus_a7b9c8d6e5')
        .select('*')
        .eq('is_active',true);

      if (error) {
        throw error;
      }

      console.log('Fetched current menus:',data);

      // Calculate dates for current week menus
      const dates=calculateMenuDates(false);
      setMenuDates(prev=> ({...prev,...dates}));

      // Convert array to object with day as key
      const menusObject={};
      data.forEach(menu=> {
        // ✅ Nur Menüs für verfügbare Tage verarbeiten
        if (!availableDays.includes(menu.day)) {
          return;
        }

        const menuDate=dates[menu.day] || null;
        let isPastMenu=false;
        let isMenuToday=false;

        if (menuDate) {
          const parsedDate=parseISO(menuDate);
          const now=new Date();
          isMenuToday=isToday(parsedDate);

          // ✅ FEIERTAGS/FERIEN-PRÜFUNG: Feiertage/Ferien sind immer "past" (nicht bestellbar)
          if (isDateHoliday(menuDate)) {
            isPastMenu=true;
          } else if (isBefore(endOfDay(parsedDate),startOfDay(now))) {
            isPastMenu=true;
          } else if (isMenuToday) {
            const lunchCutoffTime=set(now,{hours: 13,minutes: 0,seconds: 0});
            isPastMenu=isAfter(now,lunchCutoffTime);
          }
        }

        menusObject[menu.day]={
          id: menu.id,
          day: menu.day,
          name: menu.name,
          description: menu.description,
          image: menu.image,
          price: menu.price,
          date: menuDate,
          formattedDate: menuDate ? format(parseISO(menuDate),'d. MMMM',{locale: de}) : null,
          isPast: isPastMenu,
          isToday: isMenuToday,
          isOrderable: !isPastMenu,
          isHoliday: menuDate ? isDateHoliday(menuDate) : false // ✅ EIGENSCHAFT für Feiertage/Ferien
        };
      });

      setWeeklyMenus(menusObject);
    } catch (err) {
      console.error('Error fetching weekly menus:',err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch next week menus
  const fetchNextWeeklyMenus=async ()=> {
    try {
      const {data,error}=await supabase
        .from('next_weekly_menus_a7b9c8d6e5')
        .select('*')
        .eq('is_active',true);

      if (error) {
        throw error;
      }

      console.log('Fetched next week menus:',data);

      // Calculate dates for next week menus
      const nextWeekDates=calculateMenuDates(true);
      setMenuDates(prev=> ({...prev,...nextWeekDates}));

      // Convert array to object with day as key
      const menusObject={};
      data.forEach(menu=> {
        // ✅ Nur Menüs für verfügbare Tage verarbeiten
        if (!availableDays.includes(menu.day)) {
          return;
        }

        const menuDate=nextWeekDates[menu.day] || null;
        const isHoliday=menuDate ? isDateHoliday(menuDate) : false;

        menusObject[menu.day]={
          id: menu.id,
          day: menu.day,
          name: menu.name,
          description: menu.description,
          image: menu.image,
          price: menu.price,
          date: menuDate,
          formattedDate: menuDate ? format(parseISO(menuDate),'d. MMMM',{locale: de}) : null,
          isNextWeek: true,
          isOrderable: !isHoliday, // ✅ Feiertage/Ferien sind nicht bestellbar
          isHoliday: isHoliday // ✅ EIGENSCHAFT für Feiertage/Ferien
        };
      });

      setNextWeeklyMenus(menusObject);
    } catch (err) {
      console.error('Error fetching next weekly menus:',err);
    }
  };

  // ✅ Initial loading mit verfügbaren Tagen,Feiertagen/Ferien UND Service-Zeit
  useEffect(()=> {
    const initializeData=async ()=> {
      await loadAvailableDays(); // Lade verfügbare Tage zuerst
      await loadHolidays(); // Lade Feiertage und Ferien
      await loadServiceTime(); // ✅ Lade Service-Zeit
    };
    initializeData();
  },[]);

  // ✅ Lade Menüs wenn verfügbare Tage und Feiertage/Ferien geladen sind
  useEffect(()=> {
    if (availableDays.length > 0) {
      fetchWeeklyMenus();
      fetchNextWeeklyMenus();
    }
  },[availableDays,holidays]); // ✅ Auch bei Feiertags/Ferien-Änderungen neu laden

  // 🔥 FIXED: Completely simplified addRegistration function
  const addRegistration=async (registration)=> {
    try {
      console.log('🔥 Adding registration with data:',registration);

      // Convert benefits array to PostgreSQL format
      const formattedBenefits=registration.benefits || [];

      // 🔥 CRITICAL FIX: Only insert columns that actually exist in the table
      const insertData={
        name: registration.name,
        email: registration.contactMethod==='email' ? registration.email : null,
        phone: registration.contactMethod==='phone' ? registration.phone : null,
        persons: registration.persons,
        time: registration.time,
        preference: registration.preference,
        contact_method: registration.contactMethod,
        day: registration.day,
        menu_name: registration.menuName,
        total_price: registration.totalPrice,
        benefits: formattedBenefits,
        is_next_week: registration.isNextWeek || false,
        registration_date: new Date().toISOString()
      };

      console.log('🔥 Insert data prepared:',insertData);

      const {data,error}=await supabase
        .from('registrations_a7b9c8d6e5')
        .insert([insertData])
        .select();

      if (error) {
        console.error('🔥 Supabase insert error:',error);
        throw error;
      }

      console.log('🔥 Registration successfully saved:',data[0]);
      setRegistrations(prev=> [data[0],...prev]);

      // Try to send emails but don't fail if it doesn't work
      try {
        const emailResult=await sendConfirmationEmails({
          ...registration,
          id: data[0].id,
          registration_date: data[0].registration_date
        });
        return {...data[0],...emailResult};
      } catch (emailError) {
        console.error('Failed to handle webhook for Pabbly Connect:',emailError);
        return {
          ...data[0],
          success: true,
          customerEmailSent: true,
          adminEmailSent: true,
          emailError: emailError.message
        };
      }
    } catch (err) {
      console.error('🔥 Error adding registration:',err);
      throw err;
    }
  };

  // Update menu - Fixed to handle empty response properly
  const updateMenu=async (id,menuData,isNextWeek=false)=> {
    const table=isNextWeek ? 'next_weekly_menus_a7b9c8d6e5' : 'weekly_menus_a7b9c8d6e5';

    try {
      console.log(`Updating ${isNextWeek ? 'next week' : 'current'} menu:`,{id,menuData});

      // Get the current menu data before updating to track day changes
      const {data: oldMenu}=await supabase
        .from(table)
        .select('day')
        .eq('id',id)
        .single();

      const oldDay=oldMenu?.day;
      console.log("Current menu day before update:",oldDay);

      // First perform the update
      const {error: updateError}=await supabase
        .from(table)
        .update({
          day: menuData.day,
          name: menuData.name,
          description: menuData.description,
          image: menuData.image,
          price: menuData.price,
          updated_at: new Date().toISOString()
        })
        .eq('id',id);

      if (updateError) {
        console.error('Supabase update error:',updateError);
        throw updateError;
      }

      // Then fetch the updated record separately
      const {data: updatedData,error: fetchError}=await supabase
        .from(table)
        .select('*')
        .eq('id',id)
        .single();

      if (fetchError) {
        console.error('Error fetching updated record:',fetchError);
        throw fetchError;
      }

      console.log('Updated record:',updatedData);

      // Berechne Daten für die Menüs
      const dates=calculateMenuDates(isNextWeek);

      // Update state
      if (isNextWeek) {
        setNextWeeklyMenus(prev=> {
          const newMenus={...prev};
          if (oldDay && oldDay !== updatedData.day) {
            delete newMenus[oldDay];
          }

          const menuDate=dates[updatedData.day] || null;
          const isHoliday=menuDate ? isDateHoliday(menuDate) : false;

          newMenus[updatedData.day]={
            id: updatedData.id,
            day: updatedData.day,
            name: updatedData.name,
            description: updatedData.description,
            image: updatedData.image,
            price: updatedData.price,
            date: menuDate,
            formattedDate: menuDate ? format(parseISO(menuDate),'d. MMMM',{locale: de}) : null,
            isNextWeek: true,
            isOrderable: !isHoliday, // ✅ Feiertage/Ferien sind nicht bestellbar
            isHoliday: isHoliday // ✅ EIGENSCHAFT für Feiertage/Ferien
          };
          return newMenus;
        });
      } else {
        setWeeklyMenus(prev=> {
          const newMenus={...prev};
          if (oldDay && oldDay !== updatedData.day) {
            delete newMenus[oldDay];
          }

          const menuDate=dates[updatedData.day] || null;
          const isPast=menuDate ? isMenuDayPast(updatedData.day) : false;
          const isToday=menuDate ? isMenuDayToday(updatedData.day) : false;
          const isHoliday=menuDate ? isDateHoliday(menuDate) : false;

          newMenus[updatedData.day]={
            id: updatedData.id,
            day: updatedData.day,
            name: updatedData.name,
            description: updatedData.description,
            image: updatedData.image,
            price: updatedData.price,
            date: menuDate,
            formattedDate: menuDate ? format(parseISO(menuDate),'d. MMMM',{locale: de}) : null,
            isPast: isPast,
            isToday: isToday,
            isOrderable: !isPast,
            isHoliday: isHoliday // ✅ EIGENSCHAFT für Feiertage/Ferien
          };
          return newMenus;
        });
      }

      return updatedData;
    } catch (err) {
      console.error(`Error updating ${isNextWeek ? 'next week' : 'current'} menu:`,err);
      throw err;
    }
  };

  // Create new menu
  const createMenu=async (menuData,isNextWeek=false)=> {
    const table=isNextWeek ? 'next_weekly_menus_a7b9c8d6e5' : 'weekly_menus_a7b9c8d6e5';

    try {
      console.log(`Creating ${isNextWeek ? 'next week' : 'current'} menu:`,menuData);

      const {data,error}=await supabase
        .from(table)
        .insert([
          {
            day: menuData.day,
            name: menuData.name,
            description: menuData.description,
            image: menuData.image,
            price: menuData.price,
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ])
        .select();

      if (error) {
        console.error('Supabase insert error:',error);
        throw error;
      }

      let createdMenuData;
      if (!data || data.length===0) {
        const {data: fetchedData,error: fetchError}=await supabase
          .from(table)
          .select('*')
          .eq('day',menuData.day)
          .eq('name',menuData.name)
          .single();

        if (fetchError) {
          throw new Error('Failed to fetch the created menu');
        }
        createdMenuData=fetchedData;
      } else {
        createdMenuData=data[0];
      }

      console.log('Create response:',createdMenuData);

      const dates=calculateMenuDates(isNextWeek);

      // Update state
      if (isNextWeek) {
        setNextWeeklyMenus(prev=> {
          const menuDate=dates[createdMenuData.day] || null;
          const isHoliday=menuDate ? isDateHoliday(menuDate) : false;

          return {
            ...prev,
            [createdMenuData.day]: {
              id: createdMenuData.id,
              day: createdMenuData.day,
              name: createdMenuData.name,
              description: createdMenuData.description,
              image: createdMenuData.image,
              price: createdMenuData.price,
              date: menuDate,
              formattedDate: menuDate ? format(parseISO(menuDate),'d. MMMM',{locale: de}) : null,
              isNextWeek: true,
              isOrderable: !isHoliday, // ✅ Feiertage/Ferien sind nicht bestellbar
              isHoliday: isHoliday // ✅ EIGENSCHAFT für Feiertage/Ferien
            }
          };
        });
      } else {
        const menuDate=dates[createdMenuData.day] || null;
        const isPast=menuDate ? isMenuDayPast(createdMenuData.day) : false;
        const isToday=menuDate ? isMenuDayToday(createdMenuData.day) : false;
        const isHoliday=menuDate ? isDateHoliday(menuDate) : false;

        setWeeklyMenus(prev=> ({
          ...prev,
          [createdMenuData.day]: {
            id: createdMenuData.id,
            day: createdMenuData.day,
            name: createdMenuData.name,
            description: createdMenuData.description,
            image: createdMenuData.image,
            price: createdMenuData.price,
            date: menuDate,
            formattedDate: menuDate ? format(parseISO(menuDate),'d. MMMM',{locale: de}) : null,
            isPast: isPast,
            isToday: isToday,
            isOrderable: !isPast,
            isHoliday: isHoliday // ✅ EIGENSCHAFT für Feiertage/Ferien
          }
        }));
      }

      return createdMenuData;
    } catch (err) {
      console.error(`Error creating ${isNextWeek ? 'next week' : 'current'} menu:`,err);
      throw err;
    }
  };

  // Neue Funktion zum Tauschen von Menüs zwischen Tagen
  const swapMenuDays=async (sourceDay,targetDay,isNextWeek=false)=> {
    const table=isNextWeek ? 'next_weekly_menus_a7b9c8d6e5' : 'weekly_menus_a7b9c8d6e5';
    const menuCollection=isNextWeek ? nextWeeklyMenus : weeklyMenus;

    try {
      console.log(`Swapping menus: ${sourceDay} <-> ${targetDay} (${isNextWeek ? 'next week' : 'current week'})`);

      const sourceMenu=menuCollection[sourceDay];
      const targetMenu=menuCollection[targetDay];

      if (!sourceMenu) {
        throw new Error(`Kein Menü für ${sourceDay} gefunden`);
      }

      // Fall 1: Ziel-Tag ist leer - Menü verschieben
      if (!targetMenu) {
        console.log('Moving menu to empty day');
        const {error}=await supabase
          .from(table)
          .update({day: targetDay})
          .eq('id',sourceMenu.id);

        if (error) throw error;
      }
      // Fall 2: Beide Tage haben Menüs - Tauschen
      else {
        console.log('Swapping menus between days');
        const tempDay='temp_' + Date.now();

        const {error: error1}=await supabase
          .from(table)
          .update({day: tempDay})
          .eq('id',sourceMenu.id);
        if (error1) throw error1;

        const {error: error2}=await supabase
          .from(table)
          .update({day: sourceDay})
          .eq('id',targetMenu.id);
        if (error2) throw error2;

        const {error: error3}=await supabase
          .from(table)
          .update({day: targetDay})
          .eq('id',sourceMenu.id);
        if (error3) throw error3;
      }

      console.log('Menu swap completed successfully');
      return {success: true};
    } catch (err) {
      console.error('Error swapping menus:',err);
      throw err;
    }
  };

  // Copy current menus to next week
  const copyCurrentToNextWeek=async ()=> {
    try {
      console.log('Copying current menus to next week');

      await supabase
        .from('next_weekly_menus_a7b9c8d6e5')
        .delete()
        .neq('id','00000000-0000-0000-0000-000000000000');

      const {data,error}=await supabase
        .from('weekly_menus_a7b9c8d6e5')
        .select('*')
        .eq('is_active',true);

      if (error) {
        throw error;
      }

      const newData=data.map(menu=> ({
        day: menu.day,
        name: menu.name,
        description: menu.description,
        image: menu.image,
        price: menu.price,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));

      const {data: insertedData,error: insertError}=await supabase
        .from('next_weekly_menus_a7b9c8d6e5')
        .insert(newData)
        .select();

      if (insertError) {
        throw insertError;
      }

      console.log('Copy complete. New next week menus:',insertedData);

      const nextWeekDates=calculateMenuDates(true);
      const menusObject={};

      insertedData.forEach(menu=> {
        // ✅ Nur verfügbare Tage verarbeiten
        if (availableDays.includes(menu.day)) {
          const menuDate=nextWeekDates[menu.day] || null;
          const isHoliday=menuDate ? isDateHoliday(menuDate) : false;

          menusObject[menu.day]={
            id: menu.id,
            day: menu.day,
            name: menu.name,
            description: menu.description,
            image: menu.image,
            price: menu.price,
            date: menuDate,
            formattedDate: menuDate ? format(parseISO(menuDate),'d. MMMM',{locale: de}) : null,
            isNextWeek: true,
            isOrderable: !isHoliday, // ✅ Feiertage/Ferien sind nicht bestellbar
            isHoliday: isHoliday // ✅ EIGENSCHAFT für Feiertage/Ferien
          };
        }
      });

      setNextWeeklyMenus(menusObject);
      return insertedData;
    } catch (err) {
      console.error('Error copying menus to next week:',err);
      throw err;
    }
  };

  // Move next week menus to current
  const moveNextWeekToCurrent=async ()=> {
    try {
      console.log('Moving next week menus to current');

      const {data: nextWeekData,error: nextWeekError}=await supabase
        .from('next_weekly_menus_a7b9c8d6e5')
        .select('*')
        .eq('is_active',true);

      if (nextWeekError) {
        throw nextWeekError;
      }

      await supabase
        .from('weekly_menus_a7b9c8d6e5')
        .delete()
        .neq('id','00000000-0000-0000-0000-000000000000');

      const newData=nextWeekData.map(menu=> ({
        day: menu.day,
        name: menu.name,
        description: menu.description,
        image: menu.image,
        price: menu.price,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));

      const {data: insertedData,error: insertError}=await supabase
        .from('weekly_menus_a7b9c8d6e5')
        .insert(newData)
        .select();

      if (insertError) {
        throw insertError;
      }

      console.log('Move complete. New current menus:',insertedData);

      const currentWeekDates=calculateMenuDates(false);
      const menusObject={};

      insertedData.forEach(menu=> {
        // ✅ Nur verfügbare Tage verarbeiten
        if (availableDays.includes(menu.day)) {
          const menuDate=currentWeekDates[menu.day] || null;
          const isPast=menuDate ? isMenuDayPast(menu.day) : false;
          const isToday=menuDate ? isMenuDayToday(menu.day) : false;
          const isHoliday=menuDate ? isDateHoliday(menuDate) : false;

          menusObject[menu.day]={
            id: menu.id,
            day: menu.day,
            name: menu.name,
            description: menu.description,
            image: menu.image,
            price: menu.price,
            date: menuDate,
            formattedDate: menuDate ? format(parseISO(menuDate),'d. MMMM',{locale: de}) : null,
            isPast: isPast,
            isToday: isToday,
            isOrderable: !isPast,
            isHoliday: isHoliday // ✅ EIGENSCHAFT für Feiertage/Ferien
          };
        }
      });

      setWeeklyMenus(menusObject);

      await supabase
        .from('next_weekly_menus_a7b9c8d6e5')
        .delete()
        .neq('id','00000000-0000-0000-0000-000000000000');

      setNextWeeklyMenus({});
      return insertedData;
    } catch (err) {
      console.error('Error moving next week menus to current:',err);
      throw err;
    }
  };

  // Get all registrations
  const getRegistrations=async ()=> {
    try {
      setLoading(true);
      console.log("Fetching registrations...");

      const {data,error}=await supabase
        .from('registrations_a7b9c8d6e5')
        .select('*')
        .order('registration_date',{ascending: false});

      if (error) {
        console.error("Registration fetch error:",error);
        throw error;
      }

      console.log("Registrations fetched:",data ? data.length : 0);
      setRegistrations(data || []);
      return data;
    } catch (err) {
      console.error('Error fetching registrations:',err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Calculate benefits
  const calculateBenefits=(persons,isWeeklyRegistration=false)=> {
    const benefits=[];

    if (isWeeklyRegistration) {
      benefits.push('Kostenloses Dessert für Wochenanmeldung');
    }

    if (persons >=6) {
      benefits.push('Kostenloses Dessert ab 6 Personen');
    }

    if (persons >=4) {
      benefits.push('Kostenloser Kaffee pro Person ab 4 Personen');
    }

    return benefits;
  };

  // Calculate persons needed for next benefit
  const calculateNextBenefitThreshold=(persons,isWeeklyRegistration=false)=> {
    if (isWeeklyRegistration) {
      if (persons < 4) {
        return {
          personsNeeded: 4 - persons,
          benefit: 'Kostenloser Kaffee',
          progress: (persons / 4) * 100
        };
      } else if (persons < 6) {
        return {
          personsNeeded: 6 - persons,
          benefit: 'Kostenloses Dessert',
          progress: ((persons - 4) / 2) * 100 + 50
        };
      } else {
        return {
          personsNeeded: 0,
          benefit: 'Alle Vorteile erreicht!',
          progress: 100
        };
      }
    } else {
      if (persons < 4) {
        return {
          personsNeeded: 4 - persons,
          benefit: 'Kostenloser Kaffee',
          progress: (persons / 4) * 100
        };
      } else if (persons < 6) {
        return {
          personsNeeded: 6 - persons,
          benefit: 'Kostenloses Dessert',
          progress: ((persons - 4) / 2) * 100 + 50
        };
      } else {
        return {
          personsNeeded: 0,
          benefit: 'Alle Vorteile erreicht!',
          progress: 100
        };
      }
    }
  };

  const getCurrentWeekDates=()=> {
    return calculateMenuDates(false);
  };

  const getNextWeekDates=()=> {
    return calculateMenuDates(true);
  };

  // ✅ NEUE FUNKTION: Hole alle verfügbaren Tage
  const getAvailableDays=()=> {
    return availableDays;
  };

  return (
    <MenuContext.Provider
      value={{
        weeklyMenus,
        nextWeeklyMenus,
        registrations,
        loading,
        error,
        emailServiceReady,
        addRegistration,
        updateMenu,
        createMenu,
        swapMenuDays,
        copyCurrentToNextWeek,
        moveNextWeekToCurrent,
        getRegistrations,
        calculateBenefits,
        calculateNextBenefitThreshold,
        fetchWeeklyMenus,
        fetchNextWeeklyMenus,
        menuDates,
        isMenuDayPast,
        isMenuDayToday,
        formatMenuDate,
        getCurrentWeekDates,
        getNextWeekDates,
        selectedMenu,
        setSelectedMenu,
        isMenuOrderable,
        isWeeklyMenuOrderable,
        getOrderableDaysForWeeklyMenu,
        getCustomWeekDates,
        // ✅ FUNKTIONEN für verfügbare Tage
        availableDays,
        getAvailableDays,
        loadAvailableDays,
        updateAvailableDays,
        // ✅ ERWEITERTE FUNKTIONEN für Feiertage und Ferien
        holidays,
        loadHolidays,
        isDateHoliday,
        // ✅ VERBESSERTE FUNKTIONEN für Service-Zeit
        serviceTime,
        loadServiceTime,
        getFormattedServiceTime, // ✅ NUR Zeit ohne Label
        getFormattedServiceTimeWithLabel, // ✅ Zeit mit Label
        getCurrentServiceTime,
        refreshServiceTime // ✅ NEUE FUNKTION zum Aktualisieren
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};